import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

import { authContextConnector, AuthContextType } from '@harmony/pcportal-shell-library';

import { HeaderNames, HeaderValues, VirtualDirectory } from '../../../Common/Constants';
import { ErrorTypes } from '../../../Common/Constants/ErrorTypes.Constants';
import { Requests } from '../../../Common/Constants/Requests.Constants';
import { GetJwtTokenExpiryTimeInMinutes, GetNewGuid, isEmptyOrSpaces } from '../../../Utilities/Helpers';
import { filterSensitiveHeaders } from '../../../Utilities/Helpers/Telemetry.Helper';
import { AppSessionContext } from '../SessionContext/AppSessionContext';
import { ClientSessionContext } from '../SessionContext/ClientSessionContext';
import { AuthContextApi, AuthContextData, ClientSessionContextApi, ClientSessionContextData } from '../SessionContext/Models/ClientSessionContext';
import TelemetryService from '../Telemetry/TelemetryService';
import IHttpRequestService from './IHttpRequestService';
import { ClientTokenResponse } from './Models/ClientTokenResponse';

export default class HttpRequestService implements IHttpRequestService {
  private static instance: HttpRequestService;
  private telemetryService: TelemetryService;
  private appSessionContext: AppSessionContext;
  private clientSessionContext: ClientSessionContext;
  private clientSessionContextData: Promise<ClientSessionContextData | null>;
  private clientAuthContextData: Promise<AuthContextData | null>;
  private clientToken: ClientTokenResponse | undefined;

  public static getInstance = () => {
    if (!HttpRequestService.instance) {
      HttpRequestService.instance = new HttpRequestService();
    }
    return HttpRequestService.instance;
  };

  private constructor() {
    this.telemetryService = TelemetryService.getInstance();
    this.appSessionContext = AppSessionContext.getInstance();
    this.clientSessionContext = ClientSessionContext.getInstance();
    this.clientSessionContextData = this.clientSessionContext.getContext();
    this.clientAuthContextData = this.clientSessionContext.getAuthContext();
  }

  private setHeaders = async (config: AxiosRequestConfig, token: string, apiFlights?: string[], isCorpNetHeader: boolean = true) => {
    if (!config.headers) {
      return;
    }

    if (token && token !== '') {
      config.headers.Authorization = token;
    }

    config.headers.Accept = HeaderValues.ApplicationJson;
    (config.headers as any)[HeaderNames.SessionId] = this.appSessionContext.SessionId;
    (config.headers as any)[HeaderNames.PartnerCenterClient] = HeaderValues.PartnerCenterClient;
    (config.headers as any)[HeaderNames.MSRequestId] = GetNewGuid();
    (config.headers as any)[HeaderNames.MSCorrelationId] = this.appSessionContext.CorrelationId;
    (config.headers as any)[HeaderNames.Locale] = this.appSessionContext.Locale;
    (config.headers as any)[HeaderNames.CacheControl] = HeaderValues.NoCache;
    (config.headers as any)[HeaderNames.Pragma] = HeaderValues.NoCache;
    (config.headers as any)[HeaderNames.PartnerCenterSupportClient] = HeaderValues.PartnerCenterSupportClient;

    if (apiFlights) {
      (config.headers as any)[HeaderNames.Flights] = apiFlights.join('|');
    }

    if (isCorpNetHeader) {
      (config.headers as any)[HeaderNames.IsCorpNet] = (await this.clientSessionContextData)?.isCorpNet ?? false;
    }
  };

  private setPublicHeaders = async (config: AxiosRequestConfig, isCorpNetHeader: boolean = true) => {
    if (!config.headers) {
      return;
    }

    config.headers.Accept = HeaderValues.ApplicationJson;
    (config.headers as any)[HeaderNames.AccessControlAllowOrigin] = '*';
    (config.headers as any)[HeaderNames.PartnerCenterClient] = HeaderValues.PartnerCenterClient;
    (config.headers as any)[HeaderNames.MSRequestId] = GetNewGuid();
    (config.headers as any)[HeaderNames.MSCorrelationId] = this.appSessionContext.CorrelationId;
    (config.headers as any)[HeaderNames.CorrelationId] = this.appSessionContext.CorrelationId;
    (config.headers as any)[HeaderNames.Locale] = this.appSessionContext.Locale;
    (config.headers as any)[HeaderNames.SupportLocale] = this.appSessionContext.Locale;
    (config.headers as any)[HeaderNames.CacheControl] = HeaderValues.NoCache;
    (config.headers as any)[HeaderNames.Pragma] = HeaderValues.NoCache;
    (config.headers as any)[HeaderNames.PartnerCenterSupportClient] = HeaderValues.PartnerCenterSupportClient;

    (config.headers as any)[HeaderNames.IsCorpNet] = isCorpNetHeader
      ? (await this.clientSessionContextData)?.isCorpNet ?? false
      : false;
  };

  private setUnauthFlowCreateHeaders = (
    config: AxiosRequestConfig,
    xsrfRequestToken: string,
    xsrfCookieToken: string,
    apiFlights?: string[]
  ) => {
    if (!config.headers) {
      return;
    }

    config.headers.Accept = HeaderValues.ApplicationJson;
    (config.headers as any)['X-XSRF-COOKIE'] = xsrfCookieToken;
    (config.headers as any)['X-XSRF-TOKEN'] = xsrfRequestToken;
    (config.headers as any)[HeaderNames.PartnerCenterClient] = HeaderValues.PartnerCenterClient;
    (config.headers as any)[HeaderNames.MSRequestId] = GetNewGuid();
    (config.headers as any)[HeaderNames.MSCorrelationId] = this.appSessionContext.CorrelationId;
    (config.headers as any)[HeaderNames.Locale] = this.appSessionContext.Locale;
    (config.headers as any)[HeaderNames.CacheControl] = HeaderValues.NoCache;
    (config.headers as any)[HeaderNames.Pragma] = HeaderValues.NoCache;
    (config.headers as any)[HeaderNames.PartnerCenterSupportClient] = HeaderValues.PartnerCenterSupportClient;

    if (apiFlights) {
      (config.headers as any)[HeaderNames.Flights] = apiFlights.join('|');
    }
  };

  private setAdminHeader = (config: AxiosRequestConfig) => {
    (config.headers as any)[HeaderNames.IncludeOrganizationRequests] = true;
  };

  // token helper will make an api call to ui and refresh client token stored in session context
  // if the token will expire in two minutes
  public getClientToken = async (): Promise<ClientTokenResponse> => {
    const currentClientToken = (await this.clientAuthContextData)?.clientToken;
    const tokenExpireInMinutes = currentClientToken ? Math.floor(GetJwtTokenExpiryTimeInMinutes(currentClientToken)) : 0;

    if (
      tokenExpireInMinutes >= -2 &&
      (this.appSessionContext.VirtualDirectoryRoot.indexOf(VirtualDirectory.InternalTools) !== -1 ||
        this.appSessionContext.VirtualDirectoryRoot.indexOf(VirtualDirectory.SupportServiceRequests) !== -1 ||
        this.appSessionContext.VirtualDirectoryRoot.indexOf(VirtualDirectory.CustomerVNext) !== -1)
    ) {
      const response = await this.getAuthContextFd(true);
      if (response) {
        const clientToken = response.accountsFirstPartyApp ?? response.partnerCenterAppToken ?? response.PartnerCenterApp;
        this.clientSessionContext.updateClientToken(clientToken);
        return clientToken;
      }
    }

    return currentClientToken ?? '';
  };

  //Added retry logic to handle the case where the client context is rejected by other components
  private async retryOnReject<T>(fn: () => Promise<T>, retriesLeft = 3): Promise<T> {
    try {
      return await fn();
    } catch (error: any) {
      if (retriesLeft === 0) {
        this.telemetryService.traceError(ErrorTypes.clientSessionContextFailed, {
          error: error,
          message: error?.message,
        });
        throw error;
      }
      this.telemetryService.traceError(ErrorTypes.clientSessionContextRetried, {
        error: error,
        message: error?.message,
      });
      return await this.retryOnReject(fn, retriesLeft - 1);
    }
  }

  public getClientSessionContext = (): Promise<ClientSessionContextApi> => {
    if (!this.appSessionContext.IsMP) {
      return new Promise((resolve, reject) => {
        axios
          .get('api/clientsessioncontext')
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    }

    return new Promise((resolve, reject) => {
      this.retryOnReject(() =>
        this.getAuthContextFd().then(async authContext => {
          const clientToken = authContext.accountsFirstPartyApp ?? authContext.partnerCenterAppToken ?? authContext.PartnerCenterApp;
          axios
            .get(this.appSessionContext.AppConfig.siteOptions.partnerApiTrafficManagerEndpoint + '/api/clientsessioncontext', {
              headers: {
                [HeaderNames.Authorization]: clientToken ?? '',
                [HeaderNames.Accept]: HeaderValues.ApplicationJson,
                [HeaderNames.SessionId]: this.appSessionContext.SessionId,
              },
            })
            .then((response: AxiosResponse<ClientSessionContextApi | undefined>) => {
              if (response?.data) {
                response.data.clientToken = clientToken;
                resolve(response.data);
              }
              reject();
            })
            .catch((error: any) => {
              reject(error);
            });
        })
      );
    });
  };

  // It is used to get the Auth Context from FD. We are at that moment only interested in the
  public getAuthContextFd = (forceNewToken = false): Promise<AuthContextApi> => {
    return new Promise((resolve, reject) => {
      authContextConnector([AuthContextType.PartnerCenterApp], forceNewToken)
        .then(currentClientToken => {
          if (!currentClientToken) {
            throw new Error('Empty Client Token');
          }
          this.clientToken = `Bearer ${currentClientToken}`;
          resolve({ partnerCenterAppToken: this.clientToken, PartnerCenterApp: this.clientToken, accountsFirstPartyApp: this.clientToken });
        })
        .catch(error => {
          reject(error);
        });
    });
  };

  public getClientTokenRequestInstance = (includeFlightsHeader: boolean = true, includeCorpNetHeader: boolean = true, includeSupportRequestAdminHeader: boolean = false) => {
    const instance = axios.create();
    instance.interceptors.request.use(config => {
      return new Promise((resolve, reject) => {
        this.getClientToken().then(async (data: string) => {
          // reset token here
          if (!isEmptyOrSpaces(data)) {
            this.clientSessionContext.updateClientToken(data);
          }

          // const token = (await this.clientAuthContextData)?.clientToken as string;
          const token = data;
          // Not all requests require the flights to be included in the header. Apart from the Bookings call.
          let applicableFlights: string[] | undefined;
          if (includeFlightsHeader) {
            applicableFlights = (await this.clientSessionContextData)?.applicableFlights;
          }

          await this.setHeaders(config, token as string, applicableFlights, includeCorpNetHeader);

          if (includeSupportRequestAdminHeader) {
            this.setAdminHeader(config);
          }

          const eventConfig = { ...config };
          eventConfig.headers = filterSensitiveHeaders(config.headers);
          this.telemetryService.traceInformation(Requests.HttpRequest, eventConfig);
          resolve(config);
        });
      });
    });
    instance.interceptors.response.use(
      response => {
        // resetSessionIdFromResponse(response);
        const eventResponse = { ...response };
        eventResponse.headers = filterSensitiveHeaders(eventResponse.headers);
        eventResponse.config.headers = filterSensitiveHeaders(eventResponse.config.headers);
        this.telemetryService.traceInformation(Requests.HttpResponse, eventResponse);
        return response;
      },
      error => {
        //resetSessionIdFromResponse(error.response);
        const eventErrorResponse = { ...error.response };
        eventErrorResponse.headers = filterSensitiveHeaders(error.response.headers);
        eventErrorResponse.config.headers = filterSensitiveHeaders(eventErrorResponse.config.headers);
        this.telemetryService.traceError(new Error(Requests.HttpResponse), eventErrorResponse.response);
        //need to reauthenticate
        if (error && error.response && error.response.status === 401) {
          // [TODO]: add for case review and service health admin
          window.location.reload();
        }
        return Promise.reject(error);
      }
    );

    return instance;
  };

  public getPublicRequestInstance = (includeCorpNetHeader: boolean = false) => {
    const instance = axios.create();
    instance.interceptors.request.use(async config => {
      await this.setPublicHeaders(config, includeCorpNetHeader);
      const eventConfig = { ...config };
      eventConfig.headers = filterSensitiveHeaders(config.headers);
      this.telemetryService.traceInformation(Requests.HttpRequest, eventConfig);
      return config;
    });
    instance.interceptors.response.use(
      response => {
        const eventResponse = { ...response };
        eventResponse.headers = filterSensitiveHeaders(eventResponse.headers);
        eventResponse.config.headers = filterSensitiveHeaders(eventResponse.config.headers);
        this.telemetryService.traceInformation(Requests.HttpResponse, eventResponse);
        return response;
      },
      error => {
        const eventErrorResponse = { ...error.response };
        if (eventErrorResponse.headers) {
          eventErrorResponse.headers = filterSensitiveHeaders(eventErrorResponse.headers);
        }
        if (eventErrorResponse.config) {
          eventErrorResponse.config = {
            ...eventErrorResponse.config,
            headers: filterSensitiveHeaders(eventErrorResponse.config.headers),
          };
        }
        this.telemetryService.traceError(new Error(Requests.HttpResponse), eventErrorResponse);
        return Promise.reject(error);
      }
    );
    return instance;
  };

  public getUnauthCreateInstance = (xsrfRequestToken: string, xsrfCookieToken: string, apiFlights?: string[]) => {
    const instance = axios.create();
    instance.interceptors.request.use(config => {
      this.setUnauthFlowCreateHeaders(config, xsrfRequestToken, xsrfCookieToken, apiFlights);
      const eventConfig = { ...config };
      eventConfig.headers = filterSensitiveHeaders(config.headers);
      this.telemetryService.traceInformation(Requests.HttpRequest, eventConfig);
      return config;
    });
    instance.interceptors.response.use(
      response => {
        const eventResponse = { ...response };
        eventResponse.headers = filterSensitiveHeaders(eventResponse.headers);
        eventResponse.config.headers = filterSensitiveHeaders(eventResponse.config.headers);
        this.telemetryService.traceInformation(Requests.HttpResponse, eventResponse);
        return response;
      },
      error => {
        const eventErrorResponse = { ...error.response };
        if (eventErrorResponse.headers) {
          eventErrorResponse.headers = filterSensitiveHeaders(eventErrorResponse.headers);
        }
        if (eventErrorResponse.config) {
          eventErrorResponse.config = {
            ...eventErrorResponse.config,
            headers: filterSensitiveHeaders(eventErrorResponse.config.headers),
          };
        }
        this.telemetryService.traceError(new Error(Requests.HttpResponse), eventErrorResponse);
        return Promise.reject(error);
      }
    );
    return instance;
  };

  public getCDNRequestInstance = () => {
    const instance = axios.create();
    instance.interceptors.response.use(
      response => {
        return response;
      },
      error => {
        // Tracing Logic
        // Match with Panel
        return Promise.reject(error);
      }
    );
    return instance;
  };
}
